import Button from "devextreme-react/button";
import CheckBox from "devextreme-react/check-box";
import NumberBox from "devextreme-react/number-box";
import SelectBox from "devextreme-react/select-box";
import TagBox from "devextreme-react/tag-box";
import TextBox from "devextreme-react/text-box";
import { RequiredRule, Validator } from "devextreme-react/validator";
import { Tooltip } from "devextreme-react/tooltip";
import React, { useState, useEffect, useMemo } from "react";
import { DefaultPhaseDto } from "../../../dtos/DefaultPhaseDto";
import { DefaultStepDto } from "../../../dtos/DefaultStepDto";
import { DueDateBasis } from "../../../dtos/DueDateBasis";
import { GrantStatus } from "../../../dtos/GrantStatus";
import { Modules } from "../../../dtos/Modules";
import { PhaseWorkflowType } from "../../../dtos/PhaseWorkflowType";
// import { TrackedMode } from "../../../dtos/TrackedMode";
import { UserRole } from "../../../dtos/UserRole";
import { WorkflowConfigOptionsDto } from "../../../dtos/WorkflowConfigOptionsDto";
import WorkflowTypeBadge from "../WorkflowTypeBadge";
import FixedSettings from "./components/FixedSettings";
import LockedFieldHint, { TASK_FOLDER_LOCK_TEXT } from "./components/LockedFieldHint";

type Props = {
    task?: DefaultPhaseDto | null;
    subtask?: DefaultStepDto | null;
    configOptions: WorkflowConfigOptionsDto;
    onSave: (data: DefaultPhaseDto | DefaultStepDto) => Promise<void>;
};

// A checkbox with an info icon whose tooltip explains the setting.
function FlagCheckBox({ id, value, onChange, text, tooltip }: {
    id: string;
    value: boolean;
    onChange: (value: boolean) => void;
    text: string;
    tooltip: string;
}) {
    return (
        <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "6px" }}>
            <CheckBox value={value} onValueChanged={(e) => onChange(e.value)} text={text} />
            <i id={id} className="dx-icon-info" style={{ color: "#888", cursor: "help" }} aria-label={`About ${text}`} />
            <Tooltip
                target={`#${id}`}
                showEvent="mouseenter"
                hideEvent="mouseleave"
                hideOnOutsideClick={false}
                position="top"
            >
                <div style={{ padding: "8px 10px", fontSize: "12px", maxWidth: "320px", whiteSpace: "normal" }}>{tooltip}</div>
            </Tooltip>
        </div>
    );
}

export default function TemplateSettingsTab({ task, subtask, configOptions, onSave }: Props) {
    const isTask = !!task;

    // Task state
    const [name, setName] = useState(task?.name ?? subtask?.name ?? "");
    // Tasks only. Duplicates within a module are allowed, so there is no conflict check.
    const [number, setNumber] = useState(task?.number ?? 0);
    const [abbrev, setAbbrev] = useState(task?.abbrev ?? "");
    const [key, setKey] = useState(task?.key ?? "");
    const [description, setDescription] = useState(subtask?.description ?? "");
    // const [trackedMode, setTrackedMode] = useState<TrackedMode | null>(task?.settings?.trackedMode ?? null);
    const [dueAmount, setDueAmount] = useState<number>(() => {
        const dueDate = task?.settings?.dueDate;
        if (dueDate?.days) return dueDate.days;
        if (dueDate?.weeks) return dueDate.weeks;
        if (dueDate?.months) return dueDate.months;
        return 0;
    });
    const [dueUnit, setDueUnit] = useState<'days' | 'weeks' | 'months'>(() => {
        const dueDate = task?.settings?.dueDate;
        if (dueDate?.days) return 'days';
        if (dueDate?.months) return 'months';
        return 'weeks';
    });
    const [dueDateBasis, setDueDateBasis] = useState<DueDateBasis>(task?.settings?.dueDateBasis ?? DueDateBasis.ModuleCreation);
    const [visibleOnGrantStatuses, setVisibleOnGrantStatuses] = useState<GrantStatus[]>(
        task?.settings?.visibleOnGrantStatuses ?? []
    );
    // Enables the Start Resubmission action at runtime.
    const [allowResubmission, setAllowResubmission] = useState(task?.settings?.allowResubmission ?? false);
    const [extendedHeader, setExtendedHeader] = useState(task?.settings?.extendedHeader ?? false);
    const [showDownloadAll, setShowDownloadAll] = useState(task?.settings?.showDownloadAll ?? false);
    // Upload-only subtasks are a None task feature: every other type gives its subtasks a status.
    // Only DC draws the extended header, so only its tasks are offered the setting.
    const isDCTask = task?.moduleTypeId === Modules.DC;
    const folderFieldsLocked = task?.hasInstances === true;

    // Saved state for dirty tracking
    const [savedName, setSavedName] = useState(name);
    const [savedNumber, setSavedNumber] = useState(number);
    const [savedAbbrev, setSavedAbbrev] = useState(abbrev);
    const [savedKey, setSavedKey] = useState(key);
    const [savedDescription, setSavedDescription] = useState(description);
    // const [savedTrackedMode, setSavedTrackedMode] = useState(trackedMode);
    const [savedDueAmount, setSavedDueAmount] = useState(dueAmount);
    const [savedDueUnit, setSavedDueUnit] = useState(dueUnit);
    const [savedDueDateBasis, setSavedDueDateBasis] = useState(dueDateBasis);
    const [savedVisibleOnGrantStatuses, setSavedVisibleOnGrantStatuses] = useState(visibleOnGrantStatuses);
    const [savedAllowResubmission, setSavedAllowResubmission] = useState(allowResubmission);
    const [savedExtendedHeader, setSavedExtendedHeader] = useState(extendedHeader);
    const [savedShowDownloadAll, setSavedShowDownloadAll] = useState(showDownloadAll);

    // Compute dirty state
    const isDirty = useMemo(() => {
        const statusesMatch =
            visibleOnGrantStatuses.length === savedVisibleOnGrantStatuses.length &&
            visibleOnGrantStatuses.every(s => savedVisibleOnGrantStatuses.includes(s));

        return name !== savedName ||
            number !== savedNumber ||
            abbrev !== savedAbbrev ||
            key !== savedKey ||
            description !== savedDescription ||
            // trackedMode !== savedTrackedMode ||
            dueAmount !== savedDueAmount ||
            dueUnit !== savedDueUnit ||
            allowResubmission !== savedAllowResubmission ||
            extendedHeader !== savedExtendedHeader ||
            showDownloadAll !== savedShowDownloadAll ||
            dueDateBasis !== savedDueDateBasis ||
            !statusesMatch;
    }, [
        name, savedName, number, savedNumber, abbrev, savedAbbrev, key, savedKey,
        description, savedDescription,
        // trackedMode, savedTrackedMode,
        dueAmount, savedDueAmount, dueUnit, savedDueUnit,
        allowResubmission, savedAllowResubmission,
        extendedHeader, savedExtendedHeader,
        showDownloadAll, savedShowDownloadAll,
        dueDateBasis, savedDueDateBasis,
        visibleOnGrantStatuses, savedVisibleOnGrantStatuses
    ]);

    // Reset state when task/subtask changes
    useEffect(() => {
        setName(task?.name ?? subtask?.name ?? "");
        setNumber(task?.number ?? 0);
        setAbbrev(task?.abbrev ?? "");
        setKey(task?.key ?? "");
        setDescription(subtask?.description ?? "");
        // setTrackedMode(task?.settings?.trackedMode ?? null);

        const dueDate = task?.settings?.dueDate;
        if (dueDate?.days) {
            setDueAmount(dueDate.days);
            setDueUnit('days');
        } else if (dueDate?.months) {
            setDueAmount(dueDate.months);
            setDueUnit('months');
        } else if (dueDate?.weeks) {
            setDueAmount(dueDate.weeks);
            setDueUnit('weeks');
        } else {
            setDueAmount(0);
            setDueUnit('weeks');
        }

        setDueDateBasis(task?.settings?.dueDateBasis ?? DueDateBasis.ModuleCreation);

        setVisibleOnGrantStatuses(task?.settings?.visibleOnGrantStatuses ?? []);
        setAllowResubmission(task?.settings?.allowResubmission ?? false);
        setExtendedHeader(task?.settings?.extendedHeader ?? false);
        setShowDownloadAll(task?.settings?.showDownloadAll ?? false);

        // Sync saved state
        const newName = task?.name ?? subtask?.name ?? "";
        const newAbbrev = task?.abbrev ?? "";
        const newKey = task?.key ?? "";
        const newDescription = subtask?.description ?? "";
        // const newTrackedMode = task?.settings?.trackedMode ?? null;
        const newDueDateBasis = task?.settings?.dueDateBasis ?? DueDateBasis.ModuleCreation;
        const newVisibleOnGrantStatuses = task?.settings?.visibleOnGrantStatuses ?? [];

        // Use existing dueDate variable for saved state calculations
        let newDueAmount = 0;
        let newDueUnit: 'days' | 'weeks' | 'months' = 'weeks';
        if (dueDate?.days) {
            newDueAmount = dueDate.days;
            newDueUnit = 'days';
        } else if (dueDate?.months) {
            newDueAmount = dueDate.months;
            newDueUnit = 'months';
        } else if (dueDate?.weeks) {
            newDueAmount = dueDate.weeks;
            newDueUnit = 'weeks';
        }

        setSavedName(newName);
        setSavedNumber(task?.number ?? 0);
        setSavedAbbrev(newAbbrev);
        setSavedKey(newKey);
        setSavedDescription(newDescription);
        // setSavedTrackedMode(newTrackedMode);
        setSavedDueAmount(newDueAmount);
        setSavedDueUnit(newDueUnit);
        setSavedDueDateBasis(newDueDateBasis);
        setSavedVisibleOnGrantStatuses(newVisibleOnGrantStatuses);
        setSavedAllowResubmission(task?.settings?.allowResubmission ?? false);
        setSavedExtendedHeader(task?.settings?.extendedHeader ?? false);
        setSavedShowDownloadAll(task?.settings?.showDownloadAll ?? false);
    }, [task?.id, subtask?.id]);

    // Chosen in the Add Default Task dialog and locked once the task exists.
    const fixedSettings = useMemo(() => {
        if (!isTask) return [];

        const workflowType = task?.settings?.workflowType;
        const items: { label: string; value: React.ReactNode }[] = [
            {
                label: "Workflow Type",
                value: <WorkflowTypeBadge workflowType={workflowType} />
            },
            {
                label: "Tracked",
                value: task?.settings?.tracked ? "Yes" : "No"
            }
        ];

        // Only meaningful on a None task, and only ever set at creation: it decides what every
        // subtask is, and the ones a task already has keep what they were created with.
        if (workflowType === PhaseWorkflowType.None) {
            items.push({
                label: "Subtasks are document folders",
                value: task?.settings?.uploadOnlySubtasks ? "Yes" : "No"
            });
        }

        // Named for what it does rather than for the flag: this is where a subtask's uploads land
        // in SharePoint, and it is fixed at creation because the files already there cannot move.
        items.push({
            label: "Uploads land in",
            value: task?.settings?.allowRootUpload
                ? "The task's own folder, shared by every subtask"
                : "A folder per subtask"
        });

        if (key) items.push({ label: "Key", value: key });

        return items;
    }, [isTask, task?.settings?.workflowType, task?.settings?.tracked, task?.settings?.uploadOnlySubtasks, task?.settings?.allowRootUpload, key]);

    const handleSave = async () => {
        const trimmedName = name.trim();

        // The rule above shows the message; this is what actually stops the save, so a name of
        // spaces or a programmatic save cannot get past it. The server refuses it too.
        if (!trimmedName) return;

        const trimmedAbbrev = abbrev.trim();
        const trimmedKey = key.trim();
        const trimmedDescription = description.trim();

        if (isTask && task) {
            const updatedTask: DefaultPhaseDto = {
                ...task,
                name: trimmedName,
                number,
                abbrev: trimmedAbbrev,
                key: trimmedKey,
                settings: {
                    ...task.settings,
                    // trackedMode,
                    allowResubmission,
                    extendedHeader,
                    showDownloadAll,
                    dueDate: {
                        days: dueUnit === 'days' ? dueAmount : null,
                        weeks: dueUnit === 'weeks' ? dueAmount : null,
                        months: dueUnit === 'months' ? dueAmount : null
                    },
                    dueDateBasis,
                    visibleOnGrantStatuses
                }
            };
            await onSave(updatedTask);
        } else if (subtask) {
            const updatedSubtask: DefaultStepDto = {
                ...subtask,
                name: trimmedName,
                description: trimmedDescription,
                settings: {
                    ...subtask.settings
                }
            };
            await onSave(updatedSubtask);
        }

        // Sync saved state with trimmed values
        setName(trimmedName);
        setAbbrev(trimmedAbbrev);
        setKey(trimmedKey);
        setDescription(trimmedDescription);
        setSavedName(trimmedName);
        setSavedNumber(number);
        setSavedAbbrev(trimmedAbbrev);
        setSavedKey(trimmedKey);
        setSavedDescription(trimmedDescription);
        // setSavedTrackedMode(trackedMode);
        setSavedDueAmount(dueAmount);
        setSavedDueUnit(dueUnit);
        setSavedDueDateBasis(dueDateBasis);
        setSavedVisibleOnGrantStatuses(visibleOnGrantStatuses);
        setSavedAllowResubmission(allowResubmission);
        setSavedExtendedHeader(extendedHeader);
        setSavedShowDownloadAll(showDownloadAll);
    };

    const handleDiscard = () => {
        setName(savedName);
        setNumber(savedNumber);
        setAbbrev(savedAbbrev);
        setKey(savedKey);
        setDescription(savedDescription);
        // setTrackedMode(savedTrackedMode);
        setDueAmount(savedDueAmount);
        setDueUnit(savedDueUnit);
        setDueDateBasis(savedDueDateBasis);
        setVisibleOnGrantStatuses(savedVisibleOnGrantStatuses);
        setAllowResubmission(savedAllowResubmission);
        setExtendedHeader(savedExtendedHeader);
        setShowDownloadAll(savedShowDownloadAll);
    };

    return (
        <div className="template-tab">
            <div className="template-tab-body">
            {/* Tasks lead with their number, then name with the readonly workflow type badge. */}
            <div style={{ marginBottom: "16px", display: "flex", gap: "12px", alignItems: "flex-end" }}>
                {isTask && (
                    <div style={{ width: "80px" }}>
                        <label>Number{folderFieldsLocked && <LockedFieldHint id="lock-task-number" text={TASK_FOLDER_LOCK_TEXT} />}</label>
                        <NumberBox
                            value={number}
                            disabled={folderFieldsLocked}
                            onValueChanged={(e) => setNumber(e.value ?? 0)}
                            width="100%"
                            min={0}
                            showSpinButtons={false}
                            format="#0"
                        />
                    </div>
                )}
                <div style={{ flex: 1 }}>
                    <label>{isTask ? "Task Name" : "Subtask Name"}</label>
                    <TextBox
                        value={name}
                        onValueChanged={(e) => setName(e.value)}
                        width="100%"
                    >
                        <Validator>
                            <RequiredRule message={isTask ? "Task name is required" : "Subtask name is required"} />
                        </Validator>
                    </TextBox>
                </div>
            </div>

            {/* Task-specific fields */}
            {isTask && (
                <>
                    <FixedSettings items={fixedSettings} />

                    <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
                        <div style={{ flex: 1 }}>
                            <label>Abbrev (max 8 chars)</label>
                            <TextBox
                                value={abbrev}
                                onValueChanged={(e) => setAbbrev(e.value)}
                                maxLength={8}
                                width="100%"
                            />
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
                        <div style={{ flex: 1 }}>
                            <label>Due Date Offset</label>
                            <NumberBox
                                value={dueAmount}
                                onValueChanged={(e) => setDueAmount(Number(e.value))}
                                width="100%"
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label>Offset Unit</label>
                            <SelectBox
                                items={['days', 'weeks', 'months']}
                                value={dueUnit}
                                onValueChanged={(e) => setDueUnit(e.value)}
                                width="100%"
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label>Offset From</label>
                            <SelectBox
                                items={[
                                    { id: DueDateBasis.ModuleCreation, name: 'Module Creation' },
                                    { id: DueDateBasis.NoaDate, name: 'NoA Date' }
                                ]}
                                displayExpr="name"
                                valueExpr="id"
                                value={dueDateBasis}
                                onValueChanged={(e) => setDueDateBasis(e.value)}
                                width="100%"
                            />
                        </div>
                        {/* TrackedMode dropdown - disabled until rules for custom tracked tasks are defined
                        <div style={{ flex: 1 }}>
                            <label>Tracked Mode</label>
                            <SelectBox
                                items={[
                                    { id: TrackedMode.Manual, name: 'Manual (no restrictions)' },
                                    { id: TrackedMode.NoOverlap, name: 'No Overlap (validate date ranges)' },
                                    { id: TrackedMode.EndDateOnly, name: 'End Date Only (auto-cascade)' }
                                ]}
                                displayExpr="name"
                                valueExpr="id"
                                value={trackedMode}
                                onValueChanged={(e) => setTrackedMode(e.value)}
                                width="100%"
                                placeholder="Select mode..."
                                disabled={!tracked}
                            />
                        </div>
                        */}
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                        <label>Visible on Grant Statuses</label>
                        <TagBox
                            items={[
                                { id: GrantStatus.AWARDED, name: 'AWARDED' },
                                { id: GrantStatus.APPLICATION, name: 'APPLICATION' },
                                { id: GrantStatus.COUNCILREVIEW, name: 'COUNCILREVIEW' },
                                { id: GrantStatus.TOBEPAID, name: 'TOBEPAID' }
                            ]}
                            displayExpr="name"
                            valueExpr="id"
                            value={visibleOnGrantStatuses}
                            onValueChanged={(e) => setVisibleOnGrantStatuses(e.value ?? [])}
                            width="100%"
                            placeholder="Leave empty to always show..."
                            showSelectionControls={true}
                        />
                    </div>

                    <FlagCheckBox
                        id="flag-allow-resubmissions"
                        value={allowResubmission}
                        onChange={setAllowResubmission}
                        text="Allow resubmissions"
                        tooltip="Users with the Resubmit permission can start a resubmission: a fresh set of subtasks under the same task. The previous submission stays as it was, still taking files and comments."
                    />
                    {isDCTask && (
                        <FlagCheckBox
                            id="flag-extended-header"
                            value={extendedHeader}
                            onChange={setExtendedHeader}
                            text="Extended header"
                            tooltip="Shows the task's schedule row above its tasklist: the NoA offset, projected and actual start and completion dates, and comments, with the tasklist opening below it. The row also carries the task name, its status and the control that opens and closes it. Turned off, those move into the tasklist's own toolbar, the way every other module shows them."
                        />
                    )}
                    <FlagCheckBox
                        id="flag-show-download-all"
                        value={showDownloadAll}
                        onChange={setShowDownloadAll}
                        text="Show Download All"
                        tooltip="Offers a Download All action that zips every file in the task: an icon on the schedule row where the task has one, otherwise an entry in the tasklist toolbar's menu."
                    />
                </>
            )}

            {/* Subtask-specific fields */}
            {!isTask && (
                <div style={{ marginBottom: "16px" }}>
                    <label>Description</label>
                    <TextBox
                        value={description}
                        onValueChanged={(e) => setDescription(e.value)}
                        width="100%"
                    />
                </div>
            )}

            </div>

            <div className="template-tab-footer">
                <Button
                    text={isDirty ? "Save *" : "Save"}
                    type="default"
                    stylingMode="contained"
                    onClick={handleSave}
                />
                {isDirty && (
                    <Button
                        text="Discard Changes"
                        type="normal"
                        stylingMode="outlined"
                        onClick={handleDiscard}
                    />
                )}
                {isDirty && (
                    <span style={{ color: "#f0ad4e", fontSize: "12px", marginLeft: "8px" }}>
                        Unsaved changes
                    </span>
                )}
            </div>
        </div>
    );
}
